import {
  CheckIcon,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  GKButton,
  GKEditor,
  GKTextField,
  Grid2,
  InfoItalicIcon,
  Switch,
  Typography,
} from "components";
import GKCheckbox from "components/GKCheckbox";
import { FeePlansForm } from "interfaces/Revenue";
import DashboardLayout from "layouts/DashboardLayout";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useCreateFeePlanMutation } from "store/features/Revenue/FeePlan";
import useDocumentTitle from "utils/useDocumentTitle";
import { validateNotWhitespaceOnly } from "utils/validationSchema";
import { REVENUE_PRODUCT } from "../utils";
import ManagementFeeForm from "./ManagementFeeForm";
import PerformanceFeeForm from "./PerformanceFeeForm";
import SubscriptionFeeForm from "./SubscriptionFeeForm";
import { useNavigate } from "react-router-dom";
import { scrollToFirstError } from "utils/helpers";

const FeePlanForm = () => {
  useDocumentTitle("Create Fee Plan");

  const navigate = useNavigate();

  const form = useForm<FeePlansForm>({
    defaultValues: {
      name: "",
      revenueProducts: [],
      description: "",
      status: true,
      managementFee: {
        name: "",
        hsnSacCode: "",
        fee: "",
        feeType: "",
        calculationMethod: "",
        advanceType: "",
        feeParameters: "",
        billingFrequency: "",
        outOfPocketExpense: null,
        minimumFee: null,
        maximumFee: null,
        feeMethod: "Simple",
      },
      performanceFee: {
        name: "",
        hsnSacCode: "",
        fee: "",
        feeParameters: "",
        billingFrequency: "",
        outOfPocketExpense: null,
        highWatermark: false,
        minesManagementFee: false,
        includeDividend: false,
        hurdle: false,
        hurdleType: "NA",
        hurdleRate: "",
        minimumFee: null,
        maximumFee: null,
      },
      subscriptionFee: {
        name: "",
        hsnSacCode: "",
        isFree: false,
        fee: "",
        feeType: "",
        feeParameters: "",
        minimumInvestment: null,
        availableFrequency: "",
        subscriptionPlan: [
          {
            frequency: "",
            planFee: "",
            discountAmount: "",
            discountType: "Amount",
            taxableAmount: "",
            gstAmount: "",
            totalAmount: "",
          },
        ],
      },
    },
  });

  const [createFeePlan, { isLoading }] = useCreateFeePlanMutation();

  const onSubmit = async (data: FeePlansForm) => {
    const revenueProductMappings = {
      management_fee: "managementFee",
      performance_fee: "performanceFee",
      subscription_fee: "subscriptionFee",
    } as const;

    type RevenueProductKey = keyof typeof revenueProductMappings;

    const newValue: any = {
      name: data.name,
      revenueProducts: data.revenueProducts,
      status: data.status,
      description: data?.description,
    };

    data.revenueProducts.forEach((product) => {
      if (
        (Object.keys(revenueProductMappings) as RevenueProductKey[]).includes(
          product as RevenueProductKey,
        )
      ) {
        const field = revenueProductMappings[product as RevenueProductKey];
        if (field && data[field]) {
          newValue[field] = data[field];
        }
      }
    });

    try {
      await createFeePlan(newValue).unwrap();
      navigate(-1);
    } catch (err: any) {
      console.error("Create fee plan failed:", err);
    }
  };

  return (
    <DashboardLayout title="Create Fee Plan">
      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit, (errors) =>
            scrollToFirstError(errors, form),
          )}
        >
          <Grid2 container spacing={3}>
            <Grid2
              size={12}
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"center"}
              my={1}
            >
              <Typography fontSize={14} fontWeight={700} color=" #20272A">
                Set Up Fee Plan
              </Typography>
            </Grid2>
            <Grid2 container size={12} spacing={3}>
              <Grid2 size={6}>
                <Controller
                  name="name"
                  control={form.control}
                  rules={{
                    required: {
                      value: true,
                      message: "Please enter name",
                    },
                    validate: validateNotWhitespaceOnly,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <GKTextField
                      {...field}
                      requiredField
                      inputLabel="Fee Plan Name"
                      name="name"
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid2>
            </Grid2>
            <Grid2 container size={12} spacing={3}>
              <Grid2 size={6}>
                <Controller
                  name="description"
                  control={form.control}
                  render={({ field }) => (
                    <GKEditor
                      value={field.value}
                      onChange={field.onChange}
                      inputLabel="Description"
                      toolbar={[
                        "undo",
                        "redo",
                        "|",
                        "heading",
                        "bold",
                        "italic",
                        "underline",
                        "strikethrough",
                        "blockQuote",
                        "|",
                        "fontSize",
                        "fontColor",
                        "fontBackgroundColor",
                        "|",
                      ]}
                    />
                  )}
                />
                <Typography
                  display={"flex"}
                  alignItems={"center"}
                  fontSize={12}
                  fontWeight={500}
                  gap={0.5}
                  mt={1}
                >
                  <InfoItalicIcon /> The description you add here will appear in
                  the subscription agreement for this basket.
                </Typography>
              </Grid2>
            </Grid2>
            <Grid2 container size={12} spacing={3}>
              <Grid2 size={6}>
                <Controller
                  name="status"
                  control={form.control}
                  defaultValue={false}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          {...field}
                          checked={field.value}
                          color="primary"
                        />
                      }
                      label={
                        <Typography
                          fontSize={13}
                          fontWeight={600}
                          color="#333"
                          mr={1}
                        >
                          Set Plan as Active
                        </Typography>
                      }
                      labelPlacement="start"
                    />
                  )}
                />
              </Grid2>
            </Grid2>

            <Grid2 size={12}>
              <Typography fontSize={13} fontWeight={600} color="#222">
                Revenue Products:
              </Typography>
              <FormGroup row>
                {REVENUE_PRODUCT.map((option) => (
                  <Controller
                    key={option.value}
                    name="revenueProducts"
                    control={form.control}
                    render={({ field: { onChange, value } }) => {
                      const handleCheckboxChange = (
                        e: React.ChangeEvent<HTMLInputElement>,
                        optionValue: string,
                      ) => {
                        const isChecked = e.target.checked;

                        if (["subscription_fee"].includes(optionValue)) {
                          onChange(isChecked ? [optionValue] : []);
                        } else {
                          const updatedValue = isChecked
                            ? [
                                ...value.filter(
                                  (item) =>
                                    !["subscription_fee"].includes(item),
                                ),
                                optionValue,
                              ]
                            : value.filter((item) => item !== optionValue);

                          onChange(updatedValue);
                        }
                      };
                      return (
                        <FormControl component="fieldset">
                          <FormControlLabel
                            key={option.value}
                            control={
                              <GKCheckbox
                                disableLeftPadding
                                name="revenueProducts"
                                checked={value.includes(option.value)}
                                onChange={(e) =>
                                  handleCheckboxChange(e, option.value)
                                }
                              />
                            }
                            label={option.name}
                          />
                        </FormControl>
                      );
                    }}
                  />
                ))}
              </FormGroup>
            </Grid2>

            {form.watch("revenueProducts").includes("management_fee") && (
              <Grid2 size={12}>
                <ManagementFeeForm />
              </Grid2>
            )}

            {form.watch("revenueProducts").includes("performance_fee") && (
              <>
                {form.watch("revenueProducts").includes("management_fee") && (
                  <Grid2 size={12} my={2}>
                    <Divider />
                  </Grid2>
                )}
                <Grid2 size={12}>
                  <PerformanceFeeForm />
                </Grid2>
              </>
            )}

            {form.watch("revenueProducts").includes("subscription_fee") && (
              <Grid2 size={12}>
                <SubscriptionFeeForm />
              </Grid2>
            )}

            {form.getValues("revenueProducts").length !== 0 && (
              <Grid2
                size={12}
                display={"flex"}
                justifyContent={"flex-end"}
                mt={2}
              >
                <GKButton
                  variant="contained"
                  type="submit"
                  startIcon={<CheckIcon />}
                  loading={isLoading}
                >
                  Create Fee Plan
                </GKButton>
              </Grid2>
            )}
          </Grid2>
        </form>
      </FormProvider>
    </DashboardLayout>
  );
};

export default FeePlanForm;
